import { FormDialogSubmitHandler } from '@aibees/shared/components';
import { MemberStatus, MemberType } from '@aibees/shared/definition';
import { notificationIconMap } from '@aibees/shared/features/notifications/config';
import { useTransferMutationApi } from '@aibees/shared/features/user-profile/api/use-transfer-mutation-api';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { useUpdateWorkspace, useWorkspace } from '@aibees/shared/workspace';
import { Stack, Typography } from '@mui/material';
import { random } from 'lodash-es';
import { memo, useMemo } from 'react';
import { useUserManagementContext } from '../../context';
import { ChangeOwnershipForm } from '../change-ownership-form';
import { config, scheme } from './config';
import * as Styled from './styles';
import type { ChangeOwnershipDialogProps as Props } from './types';

export const ChangeOwnershipDialog = memo(
  ({ className, item, onClose }: Props) => {
    const workspace = useWorkspace();
    const { inviteFormVisible } = useUserManagementContext();
    const api = useTransferMutationApi();
    const notify = useNotify();
    const updateWorkspace = useUpdateWorkspace();

    const hasAnyActiveMembers = useMemo(
      () =>
        !!workspace?.members.some(
          (member) =>
            member.status === MemberStatus.Active &&
            member.type !== MemberType.Owner
        ),
      [workspace?.members]
    );

    const handleSubmit: FormDialogSubmitHandler<{ newOwner: string }> = (
      data,
      e
    ) => {
      if (hasAnyActiveMembers) {
        e.startProgress();
        const foundMember = workspace?.members.find(
          (member) => data.newOwner === member.user?.id
        );
        const user = foundMember
          ? {
              label: ` ${foundMember.user?.username}`,
              value: foundMember.user?.id,
            }
          : null;

        api
          .mutateAsync({ transfer: { user: { id: data.newOwner } } })
          .then((updatedWorkspace) => {
            updateWorkspace(updatedWorkspace);
            e.toggle();
            notify.complete(
              'Ownership successfully transferred',
              `Ownership transfer to <b>${user?.label}</b> completed successfully.`,
              random(1, 100),
              {
                icon: notificationIconMap.UserManagement!,
              }
            );
          })
          .catch(() => {
            e.endProgress();
          });
      } else {
        onClose();
        inviteFormVisible.onToggle();
      }
    };

    return (
      <Styled.Wrapper
        className={classnames('ChangeOwnershipDialog', className)}
        {...config}
        scheme={hasAnyActiveMembers ? scheme : undefined}
        defaultOpen
        onClose={onClose}
        onSubmit={handleSubmit}
        submitButtonProps={{
          children: hasAnyActiveMembers ? 'Transfer' : 'Invite Team Members',
        }}
        maxWidth="xs"
        fullWidth
      >
        {hasAnyActiveMembers ? (
          <ChangeOwnershipForm item={item} />
        ) : (
          <Stack sx={{ paddingX: 3 }}>
            <Typography variant="body4" color="grey.700">
              Transfer of ownership is only possible to active team members.
              Please ensure if there are deactivated members in your team to be
              reactivated or extend an invitation to the individual you wish to
              transfer ownership to so that they can join your team.
            </Typography>
            <Typography variant="body4" color="grey.700">
              After joining your team you are able to transfer the ownership to
              them.
            </Typography>
          </Stack>
        )}
      </Styled.Wrapper>
    );
  }
);

ChangeOwnershipDialog.displayName = 'ChangeOwnershipDialog';
